import type { RecipientStatus } from '@sportninja/common/services/socialFeed'

/**
 * Audience-group keys as the API names them, in human words.
 *
 * Keyed off the server's own group names rather than the role-type ids the compose form
 * uses — a recipient's group is decided by the resolver (most senior role wins), so the
 * two vocabularies are not interchangeable.
 */
export const GROUP_LABELS: Record<string, string> = {
  owners: 'Owner',
  admins: 'Admin',
  league_admins: 'League Manager',
  videographers: 'Videographer',
  officials: 'Official',
  team_officials: 'Team Official',
  players: 'Player',
  following: 'Follower',
}

export const groupLabel = (group: string): string =>
  GROUP_LABELS[group] ?? group

/**
 * The same words the roster uses, wherever the roster has a word for it.
 *
 * `PlayerSection`, `TeamOfficialSection`, `PlayerListTable` and the rest render Accepted /
 * Invited off `is_accepted` / `is_invited`, which resolve to `invitation.accepted_at`.
 * This list is a different view of the same people, so it says the same thing about them.
 * `No email` describes DELIVERY rather than invite standing and the roster has no word for
 * it, so it keeps its own — inventing a roster-sounding one would trade a visible
 * inconsistency for an invisible lie.
 *
 * Four since the Sep 4 QA call: `guardian_linked` and `email_opted_out` fold into
 * Accepted. Kept in lockstep with SocialFeedService::STATUS_LABELS, which the CSV reads.
 */
export const STATUS_LABELS: Record<RecipientStatus, string> = {
  joined: 'Accepted',
  invited: 'Invited',
  not_invited: 'Not Invited',
  no_email: 'No email',
}

export const statusLabel = (status: RecipientStatus): string =>
  STATUS_LABELS[status] ?? status
